import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Popover, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { CommonTextField } from "src/components/common-textfield";
import { RegionSearchTextBox } from "src/components/region-search-textbox";
import { aptApi } from "src/api/apt";
import { DataRanges, DataTypes } from "src/utils/configs";
import { getTypeColor, formatNumber } from "src/utils/util";
import { formatAptAge } from "src/utils/apt-age";
import { AptLinkButtons } from "src/components/apt-link-buttons";
import { LabeledSwitch } from "src/components/labled-switch";
import {
  selectRepresentativePyeongs,
  sortAllPyeongs,
} from "src/components/apt-search-pyeong-results";

export const AptSearchTextBox = ({
  onAptSelect,
  regionSearchProps,
  width = 120,
  placeholder = "아파트검색...",
  dataRange = DataRanges.APT, // APT 또는 SPYEONG
}) => {
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAllPyeongs, setShowAllPyeongs] = useState(false);
  const anchorRef = useRef(null);
  const requestIdRef = useRef(0);
  const searchTimerRef = useRef(null);
  useEffect(() => () => {
    requestIdRef.current += 1;
    clearTimeout(searchTimerRef.current);
  }, []);

  const closeSearch = useCallback(() => {
    clearTimeout(searchTimerRef.current);
    requestIdRef.current += 1;
    setSearchText("");
    setSearchResults([]);
    setLoading(false);
  }, []);

  const handleRegionSelect = useCallback((region) => {
    regionSearchProps?.onRegionSelect?.(region);
    closeSearch();
  }, [regionSearchProps, closeSearch]);

  /** SPYEONG 검색은 기본적으로 아파트별 대표 평형만 보여주고 스위치로 전체를 전환한다. */
  const displayResults = useMemo(() => {
    if (dataRange !== DataRanges.SPYEONG) return searchResults;
    return showAllPyeongs
      ? sortAllPyeongs(searchResults)
      : selectRepresentativePyeongs(searchResults);
  }, [dataRange, searchResults, showAllPyeongs]);

  // Ignore responses from a previous query or a dismissed search.
  const handleSearch = useCallback(async (query) => {
    const requestId = ++requestIdRef.current;
    setSearchText(query);
    setSearchResults([]);

    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // dataRange에 따라 다른 API 호출
      const searchFn = dataRange === DataRanges.SPYEONG
        ? aptApi.searchSupplyPyeongs
        : aptApi.searchApts;
      const response = dataRange === DataRanges.SPYEONG
        ? await searchFn(query, { page: 0, rowsPerPage: 100 })
        : await searchFn(query);
      if (requestId === requestIdRef.current) setSearchResults(response.content || []);
    } catch (error) {
      console.error("아파트 검색 에러:", error);
      if (requestId === requestIdRef.current) setSearchResults([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [dataRange]);

  const handleInputChange = useCallback((query) => {
    clearTimeout(searchTimerRef.current);
    requestIdRef.current += 1;
    setSearchText(query);
    setSearchResults([]);
    const canSearch = query.trim().length >= 2;
    setLoading(canSearch);
    if (canSearch) {
      searchTimerRef.current = setTimeout(() => handleSearch(query.trim()), 500);
    }
  }, [handleSearch]);

  const handleSelect = useCallback(
    (apt) => {
      if (onAptSelect) {
        onAptSelect(apt);
      }
      closeSearch();
    },
    [onAptSelect, closeSearch]
  );

  const highlightMatches = useCallback((text, matchedKeywords) => {
    if (!matchedKeywords || matchedKeywords.length === 0) {
      return text;
    }

    const parts = [];
    let lastIndex = 0;
    const lowerText = text.toLowerCase();
    const matches = [];

    for (const keyword of matchedKeywords) {
      const lowerKeyword = keyword.toLowerCase();
      let index = lowerText.indexOf(lowerKeyword);
      while (index !== -1) {
        matches.push({ start: index, end: index + keyword.length, keyword });
        index = lowerText.indexOf(lowerKeyword, index + 1);
      }
    }

    matches.sort((a, b) => a.start - b.start);

    for (const match of matches) {
      if (match.start >= lastIndex) {
        if (match.start > lastIndex) {
          parts.push({ text: text.slice(lastIndex, match.start), bold: false });
        }
        parts.push({ text: text.slice(match.start, match.end), bold: true });
        lastIndex = match.end;
      }
    }

    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex), bold: false });
    }

    return parts.length > 0 ? parts : [{ text, bold: false }];
  }, []);

  return (
    <>
      <Box
        ref={anchorRef}
        sx={width === "100%" ? { width: "100%", minWidth: 0 } : undefined}
      >
        <CommonTextField
          text={searchText}
          placeholder={placeholder}
          onTextChanged={handleInputChange}
          debounceMs={0}
          width={width}
        />
      </Box>
      <Popover
        open={searchText.trim().length >= 2}
        anchorEl={anchorRef.current}
        onClose={closeSearch}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableAutoFocus
        disableEnforceFocus
      >
        {searchText.trim().length >= 2 && (
          <Stack
            direction="column"
            spacing={1}
            sx={{ width: 420, maxWidth: "calc(100vw - 32px)", maxHeight: "70vh", overflowY: "auto", p: 1 }}
          >
            {regionSearchProps && (
              <>
                <Typography variant="subtitle2">지역</Typography>
                <RegionSearchTextBox
                  {...regionSearchProps}
                  query={searchText}
                  resultsOnly
                  onRegionSelect={handleRegionSelect}
                />
                <Typography variant="subtitle2">아파트</Typography>
              </>
            )}
            {loading && (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                검색 중...
              </Typography>
            )}
            {dataRange === DataRanges.SPYEONG && !loading && (
              <Stack
                direction="row"
                justifyContent="flex-end"
              >
                <LabeledSwitch
                  label="전체 평형"
                  checked={showAllPyeongs}
                  onChange={setShowAllPyeongs}
                />
              </Stack>
            )}
            {displayResults.length > 0 && (
              <Box
                sx={{
                  maxHeight: 300,
                  overflowY: "auto",
                }}
              >
                {displayResults.map((apt) => {
                  // SPYEONG일 때는 평형 정보 포함
                  const isSPyeong = apt.dataRange === DataRanges.SPYEONG;
                  const pyeongSuffix = isSPyeong && apt.pyeong ? ` (${apt.pyeong}평)` : "";
                  const displayName = `${apt.dongName} ${apt.aptName}${pyeongSuffix}`;
                  const subInfo = `${apt.sidoName} ${apt.gunguName}`;
                  const parts = highlightMatches(displayName, apt.matchedKeywords);
                  // SPYEONG일 때는 pyeongId를 key로 사용
                  const itemKey = isSPyeong ? apt.pyeongId : apt.aptId;

                  return (
                    <Box
                      key={itemKey}
                      onClick={() => handleSelect(apt)}
                      sx={{
                        p: 1,
                        cursor: "pointer",
                        borderBottom: "1px solid #eee",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {Array.isArray(parts)
                            ? parts.map((part, i) =>
                                part.bold ? (
                                  <strong key={i}>{part.text}</strong>
                                ) : (
                                  <span key={i}>{part.text}</span>
                                )
                              )
                            : displayName}
                        </Typography>
                        {/* 링크 버튼은 행 선택을 전파하지 않으며 새창, 네이버, 지도검색, 별표 순서로 표시한다.
                            SPYEONG은 선택 평형을 새창과 관심아파트에 그대로 전달한다. */}
                        <AptLinkButtons
                          aptId={apt.aptId}
                          pyeongId={isSPyeong ? apt.pyeongId : undefined}
                          supplyPyeong={isSPyeong ? apt.pyeong : undefined}
                          aptName={apt.aptName}
                          showStar
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {subInfo} | {apt.totalHousehold}세대 | {formatAptAge(apt.ages, "년차", "-", apt.approvedDate)}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                      >
                        {apt.dealPriceAvr && (
                          <Typography
                            variant="caption"
                            sx={{ color: getTypeColor(DataTypes.DEAL, 1) }}
                          >
                            매매시세 {formatNumber(apt.dealPriceAvr * 10000)}
                          </Typography>
                        )}
                        {apt.dealRealPriceAvr && (
                          <Typography
                            variant="caption"
                            sx={{ color: getTypeColor(DataTypes.DEAL, 1) }}
                          >
                            매매실거래 {formatNumber(apt.dealRealPriceAvr * 10000)}
                          </Typography>
                        )}
                        {apt.leasePriceAvr && (
                          <Typography
                            variant="caption"
                            sx={{ color: getTypeColor(DataTypes.LEASE, 1) }}
                          >
                            전세시세 {formatNumber(apt.leasePriceAvr * 10000)}
                          </Typography>
                        )}
                        {apt.leaseRealPriceAvr && (
                          <Typography
                            variant="caption"
                            sx={{ color: getTypeColor(DataTypes.LEASE, 1) }}
                          >
                            전세실거래 {formatNumber(apt.leaseRealPriceAvr * 10000)}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            )}
            {!loading && displayResults.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                검색 결과가 없습니다.
              </Typography>
            )}
          </Stack>
        )}
      </Popover>
    </>
  );
};
